import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Loader2, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface DominantStrategy {
  type: "clear_winner" | "conditional" | "trade_off" | "hidden_winner";
  option_name?: string;
  explanation: string;
  key_insights: string[];
  action_steps?: string[];
  risks_to_watch?: string[];
}

interface DominantStrategySummaryProps {
  dominantStrategy: DominantStrategy | null;
  isLoading?: boolean;
}

export const DominantStrategySummary = ({ 
  dominantStrategy, 
  isLoading 
}: DominantStrategySummaryProps) => {
  if (isLoading) {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardContent className="py-6">
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Analysiere...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!dominantStrategy) {
    return null;
  }

  const getStrategyBadge = () => {
    switch (dominantStrategy.type) {
      case "clear_winner":
        return { label: "Klarer Favorit", color: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" };
      case "conditional":
        return { label: "Bedingt", color: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20" };
      case "trade_off":
        return { label: "Trade-Off", color: "bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20" };
      case "hidden_winner":
        return { label: "Überraschung", color: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20" };
      default:
        return { label: "Analyse", color: "bg-muted text-muted-foreground border-muted" };
    }
  };

  const badge = getStrategyBadge();

  return (
    <Card className="border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl bg-primary/10 shrink-0">
            <Trophy className="h-5 w-5 text-primary" />
          </div>
          <div className="space-y-3 flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {dominantStrategy.option_name ? (
                  <h3 className="text-lg font-semibold leading-tight">
                    <span className="text-primary">{dominantStrategy.option_name}</span> ist dominant
                  </h3>
                ) : (
                  <h3 className="text-lg font-semibold">Strategische Analyse</h3>
                )}
              </div>
              <Badge variant="outline" className={cn("text-[10px] shrink-0", badge.color)}>
                {badge.label}
              </Badge>
            </div>
            
            {/* Explanation */}
            <p className="text-sm text-muted-foreground leading-relaxed">
              {dominantStrategy.explanation}
            </p>

            {/* Key Insights - compact */}
            {dominantStrategy.key_insights.length > 0 && (
              <ul className="space-y-1">
                {dominantStrategy.key_insights.slice(0, 3).map((insight, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                    <span className="text-foreground">{insight}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
